import { addDaysToDate, addMonthsToDate, compareDates, daysBetween, formatDate, monthEndAfter, parseDate, } from '../dateString.js';
import { allAmounts, cappedAmount, constNum, describeAmount, firstAmount, giveAmount, literalStart, once, repeat, scaleAmount, unit, whenAmount, withinCap, zero, } from './constructors.js';
import { firstOccurrence, } from './dsl.js';
function evaluateDateObservable(obs, ctx) {
    switch (obs.kind) {
        case 'const':
            return obs.value;
        case 'context':
            return ctx.dates?.[obs.dateName];
        case 'startOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset);
            d.setDate(1);
            return formatDate(d);
        }
        case 'endOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset + 1);
            d.setDate(0);
            return formatDate(d);
        }
        case 'dayOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset);
            d.setDate(obs.day);
            return formatDate(d);
        }
        case 'addDays': {
            const base = evaluateDateObservable(obs.base, ctx);
            return base === undefined ? undefined : addDaysToDate(base, obs.days);
        }
    }
}
function evaluateNumericObservable(obs, ctx) {
    switch (obs.kind) {
        case 'const':
            return obs.value;
        case 'daysLate': {
            if (!ctx.dueDate)
                return 0;
            const daysLate = daysBetween(ctx.dueDate, ctx.currentDate) - obs.gracePeriod;
            return Math.max(0, daysLate);
        }
        case 'context':
            return ctx.measures?.[obs.factName] ?? 0;
        case 'multiply':
            return evaluateNumericObservable(obs.left, ctx) * evaluateNumericObservable(obs.right, ctx);
        case 'max':
            if (obs.values.length === 0) {
                throw new Error("Cannot evaluate 'max' of empty values array");
            }
            return Math.max(...obs.values.map((v) => evaluateNumericObservable(v, ctx)));
        case 'min':
            if (obs.values.length === 0) {
                throw new Error("Cannot evaluate 'min' of empty values array");
            }
            return Math.min(...obs.values.map((v) => evaluateNumericObservable(v, ctx)));
    }
}
function evaluateCondition(cond, ctx) {
    switch (cond.kind) {
        case 'const':
            return cond.value;
        case 'after': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) > 0;
        }
        case 'before': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) < 0;
        }
        case 'onOrAfter': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) >= 0;
        }
        case 'onOrBefore': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) <= 0;
        }
        case 'isLate': {
            if (!ctx.dueDate)
                return false;
            const daysLate = daysBetween(ctx.dueDate, ctx.currentDate);
            return daysLate > cond.gracePeriod;
        }
        case 'and':
            return cond.conditions.every((c) => evaluateCondition(c, ctx));
        case 'or':
            return cond.conditions.some((c) => evaluateCondition(c, ctx));
        case 'not':
            return !evaluateCondition(cond.condition, ctx);
        case 'context':
            return ctx.facts?.[cond.factName] ?? false;
    }
}
function evaluateAmountSpec(spec, ctx) {
    switch (spec.kind) {
        case 'unit':
            return { amount: 1 };
        case 'scale': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            const factor = evaluateNumericObservable(spec.factor, ctx);
            return { ...inner, amount: inner.amount * factor };
        }
        case 'give': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return { ...inner, amount: -inner.amount };
        }
        case 'when': {
            const conditionTrue = evaluateCondition(spec.condition, ctx);
            if (!conditionTrue)
                return { amount: 0 };
            return evaluateAmountSpec(spec.amount, ctx);
        }
        case 'all': {
            let totalAmount = 0;
            let description = undefined;
            let classification = undefined;
            for (const s of spec.amounts) {
                const result = evaluateAmountSpec(s, ctx);
                totalAmount += result.amount;
                if (!description && result.description)
                    description = result.description;
                if (!classification && result.classification)
                    classification = result.classification;
            }
            const result = { amount: totalAmount };
            if (description)
                result.description = description;
            if (classification)
                result.classification = classification;
            return result;
        }
        case 'first': {
            for (const s of spec.amounts) {
                const result = evaluateAmountSpec(s, ctx);
                if (result.amount !== 0)
                    return result;
            }
            return { amount: 0 };
        }
        case 'capped': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            const maxAmount = evaluateNumericObservable(spec.maxAmount, ctx);
            return { ...inner, amount: Math.min(inner.amount, maxAmount) };
        }
        case 'describe': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            const result = {
                ...inner,
                description: spec.description,
            };
            if (spec.classification !== undefined) {
                result.classification = spec.classification;
            }
            return result;
        }
        case 'surcharge': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            const rate = evaluateNumericObservable(spec.rate, ctx);
            return { ...inner, amount: inner.amount + inner.amount * rate };
        }
    }
}
function occurrenceAt(recurrence, start, index) {
    switch (recurrence.kind) {
        case 'once':
            return start;
        case 'daily':
            return addDaysToDate(start, index * recurrence.interval);
        case 'weekly':
            return addDaysToDate(start, index * 7 * recurrence.interval);
        case 'monthly':
            return addMonthsToDate(start, index * recurrence.interval);
        case 'yearly':
            return addMonthsToDate(start, index * 12 * recurrence.interval);
        case 'monthEnd':
            return monthEndAfter(start, index * recurrence.interval);
    }
}
function nextStart(recurrence, start) {
    const next = occurrenceAt(recurrence, start, 1);
    const anchorDay = recurrence.kind === 'monthly'
        ? recurrence.dayOfMonth
        : recurrence.kind === 'yearly'
            ? recurrence.day
            : undefined;
    if (anchorDay === undefined || dayInMonth(start, anchorDay) !== parseDate(start).getDate()) {
        return next;
    }
    const date = parseDate(next);
    date.setDate(dayInMonth(next, anchorDay));
    return formatDate(date);
}
function dayInMonth(ds, day) {
    const d = parseDate(ds);
    return Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate());
}
function collectPayments(payment, ctx, windowEnd) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'once': {
            const dueDate = evaluateDateObservable(payment.date, ctx);
            if (dueDate === undefined)
                return [];
            if (compareDates(dueDate, windowEnd) > 0)
                return [];
            const amountResult = evaluateAmountSpec(payment.amount, { ...ctx, dueDate });
            if (amountResult.amount === 0)
                return [];
            return [
                {
                    dueDate,
                    amount: amountResult.amount,
                    description: amountResult.description,
                    classification: amountResult.classification,
                },
            ];
        }
        case 'repeat': {
            const results = [];
            const start = evaluateDateObservable(payment.startDate, ctx);
            if (start === undefined)
                return [];
            const occurrenceDate = (index) => occurrenceAt(payment.recurrence, start, index);
            let currentStart = occurrenceDate(0);
            const end = payment.endDate ?? windowEnd;
            let occurrences = 0;
            const maxOccurrences = payment.maxOccurrences ?? 1000;
            while (compareDates(currentStart, end) <= 0 &&
                compareDates(currentStart, windowEnd) <= 0 &&
                occurrences < maxOccurrences) {
                const occurrenceCtx = {
                    ...ctx,
                    dueDate: currentStart,
                };
                const amountResult = evaluateAmountSpec(payment.amount, occurrenceCtx);
                if (amountResult.amount !== 0) {
                    results.push({
                        dueDate: currentStart,
                        amount: amountResult.amount,
                        description: amountResult.description,
                        classification: amountResult.classification,
                    });
                }
                occurrences++;
                currentStart =
                    payment.recurrence.kind === 'once'
                        ? addDaysToDate(end, 1)
                        : occurrenceDate(occurrences);
            }
            return results;
        }
        case 'all':
            return payment.payments.flatMap((p) => collectPayments(p, ctx, windowEnd));
        case 'first': {
            for (const p of payment.payments) {
                const results = collectPayments(p, ctx, windowEnd);
                if (results.length > 0)
                    return results;
            }
            return [];
        }
        case 'notToExceed':
            return withinCap(collectPayments(payment.payment, ctx, windowEnd), payment.maxAmount, payment.period);
    }
}
export function getPaymentsDue(payment, currentDate, assumptions = {}) {
    const ctx = { currentDate, ...assumptions };
    const windowEnd = addMonthsToDate(currentDate, 12);
    const allPayments = collectPayments(payment, ctx, windowEnd);
    allPayments.sort((a, b) => compareDates(a.dueDate, b.dueDate));
    const overdue = [];
    let upcoming = null;
    for (const p of allPayments) {
        if (compareDates(p.dueDate, currentDate) <= 0) {
            overdue.push(p);
        }
        else if (!upcoming) {
            upcoming = p;
        }
    }
    return { overdue, upcoming };
}
export function getPaymentsInRange(payment, startDate, endDate, assumptions = {}) {
    const ctx = { currentDate: startDate, ...assumptions };
    const allPayments = collectPayments(payment, ctx, endDate);
    return allPayments.filter((p) => compareDates(p.dueDate, startDate) >= 0 && compareDates(p.dueDate, endDate) <= 0);
}
const PROJECTION_OCCURRENCE_GUARD = 4000;
export function projectedDueDates(payment, windowStart, windowEnd, asOfDate) {
    const inWindow = (date) => compareDates(date, windowStart) >= 0 && compareDates(date, windowEnd) <= 0;
    switch (payment.kind) {
        case 'once': {
            const dueDate = evaluateDateObservable(payment.date, { currentDate: asOfDate, facts: {} });
            return dueDate !== undefined && inWindow(dueDate) ? [dueDate] : [];
        }
        case 'repeat': {
            const dates = [];
            const end = payment.endDate ?? windowEnd;
            const countsHistory = payment.maxOccurrences !== undefined;
            const limit = payment.maxOccurrences ?? PROJECTION_OCCURRENCE_GUARD;
            let counted = 0;
            const start = literalStart(payment);
            if (start === undefined)
                return [];
            let index = 0;
            let current = occurrenceAt(payment.recurrence, start, index);
            while (compareDates(current, end) <= 0 &&
                compareDates(current, windowEnd) <= 0 &&
                counted < limit) {
                if (inWindow(current))
                    dates.push(current);
                if (countsHistory || inWindow(current))
                    counted++;
                if (payment.recurrence.kind === 'once')
                    return dates;
                index++;
                const next = occurrenceAt(payment.recurrence, start, index);
                if (compareDates(next, current) <= 0)
                    return dates;
                current = next;
            }
            return dates;
        }
        default: {
            const _exhaustive = payment;
            throw new Error(`Unhandled payment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
function createLineItem(amount, description, classification) {
    if (amount === 0)
        return [];
    const item = { amount, description };
    if (classification !== undefined)
        item.classification = classification;
    return [item];
}
function scaleLines(lines, factor) {
    return lines.flatMap((line) => createLineItem(line.amount * factor, line.description, line.classification));
}
export function extractLineItems(spec, ctx) {
    const evalCtx = { currentDate: ctx.currentDate };
    if (ctx.dueDate !== undefined)
        evalCtx.dueDate = ctx.dueDate;
    if (ctx.facts !== undefined)
        evalCtx.facts = ctx.facts;
    function walk(s) {
        switch (s.kind) {
            case 'describe': {
                const inner = evaluateAmountSpec(s.amount, evalCtx);
                return createLineItem(inner.amount, s.description, s.classification);
            }
            case 'all':
                return s.amounts.flatMap(walk);
            case 'surcharge': {
                const base = evaluateAmountSpec(s.amount, evalCtx);
                const rate = evaluateNumericObservable(s.rate, evalCtx);
                return [
                    ...walk(s.amount),
                    ...createLineItem(base.amount * rate, s.description, s.classification),
                ];
            }
            case 'when':
                return evaluateCondition(s.condition, evalCtx) ? walk(s.amount) : [];
            case 'scale':
                return scaleLines(walk(s.amount), evaluateNumericObservable(s.factor, evalCtx));
            case 'give':
                return scaleLines(walk(s.amount), -1);
            case 'first': {
                const paying = s.amounts.find((part) => evaluateAmountSpec(part, evalCtx).amount !== 0);
                return paying === undefined ? [] : walk(paying);
            }
            case 'capped': {
                const lines = walk(s.amount);
                const total = lines.reduce((sum, line) => sum + line.amount, 0);
                const capped = Math.min(total, evaluateNumericObservable(s.maxAmount, evalCtx));
                return total === 0 || capped === total ? lines : scaleLines(lines, capped / total);
            }
            case 'unit':
                return createLineItem(1, '');
        }
    }
    return walk(spec);
}
export function applyPayment(payment, amount, paymentDate, facts = {}) {
    return applyPaymentInternal(payment, amount, paymentDate, facts);
}
function applyPaymentInternal(payment, amount, paymentDate, facts) {
    switch (payment.kind) {
        case 'zero':
            return payment;
        case 'once': {
            const ctx = { currentDate: paymentDate, facts };
            const due = collectPayments(payment, ctx, paymentDate);
            if (due.length > 0 && Math.abs(due[0].amount - amount) < 0.01) {
                return zero();
            }
            return payment;
        }
        case 'repeat': {
            const start = literalStart(payment);
            if (start === undefined)
                return payment;
            const ctx = {
                currentDate: paymentDate,
                dueDate: firstOccurrence(payment.recurrence, start),
                facts,
            };
            const amountResult = evaluateAmountSpec(payment.amount, ctx);
            if (Math.abs(amountResult.amount - amount) < 0.01) {
                if (payment.recurrence.kind === 'once')
                    return zero();
                const newStart = nextStart(payment.recurrence, start);
                if (payment.endDate && compareDates(newStart, payment.endDate) > 0) {
                    return zero();
                }
                const result = {
                    ...payment,
                    startDate: { kind: 'const', value: newStart },
                };
                if (payment.maxOccurrences) {
                    result.maxOccurrences = payment.maxOccurrences - 1;
                }
                return result;
            }
            return payment;
        }
        case 'all': {
            let remaining = amount;
            const newPayments = [];
            for (const p of payment.payments) {
                if (remaining <= 0) {
                    newPayments.push(p);
                    continue;
                }
                const ctx = { currentDate: paymentDate, facts };
                const due = collectPayments(p, ctx, paymentDate);
                const dueAmount = due.reduce((sum, d) => sum + d.amount, 0);
                if (dueAmount > 0 && remaining >= dueAmount) {
                    newPayments.push(applyPaymentInternal(p, dueAmount, paymentDate, facts));
                    remaining -= dueAmount;
                }
                else {
                    newPayments.push(p);
                }
            }
            return simplifyAllPayments(newPayments);
        }
        case 'first':
            return {
                ...payment,
                payments: payment.payments.map((p) => applyPaymentInternal(p, amount, paymentDate, facts)),
            };
        case 'notToExceed':
            return payment;
    }
}
function simplifyAllPayments(payments) {
    const nonZero = payments.filter((p) => p.kind !== 'zero');
    if (nonZero.length === 0)
        return zero();
    if (nonZero.length === 1)
        return nonZero[0];
    return { kind: 'all', payments: nonZero };
}
export function advancePeriod(payment, toDate, facts = {}) {
    return normalizePayment(payment, { currentDate: toDate, facts });
}
function normalizeAmountSpec(spec, ctx) {
    switch (spec.kind) {
        case 'unit':
            return spec;
        case 'scale': {
            const normalizedInner = normalizeAmountSpec(spec.amount, ctx);
            const factor = evaluateNumericObservable(spec.factor, ctx);
            return scaleAmount(factor, normalizedInner);
        }
        case 'give': {
            const normalizedInner = normalizeAmountSpec(spec.amount, ctx);
            return giveAmount(normalizedInner);
        }
        case 'when': {
            const conditionResult = evaluateCondition(spec.condition, ctx);
            if (conditionResult) {
                return normalizeAmountSpec(spec.amount, ctx);
            }
            else {
                if (isConditionPermanentlyFalse(spec.condition, ctx)) {
                    return scaleAmount(0, unit());
                }
                return whenAmount(spec.condition, normalizeAmountSpec(spec.amount, ctx));
            }
        }
        case 'all': {
            const normalized = spec.amounts.map((s) => normalizeAmountSpec(s, ctx));
            return allAmounts(...normalized);
        }
        case 'first': {
            const normalized = spec.amounts.map((s) => normalizeAmountSpec(s, ctx));
            return firstAmount(...normalized);
        }
        case 'capped': {
            const normalizedInner = normalizeAmountSpec(spec.amount, ctx);
            const maxAmount = evaluateNumericObservable(spec.maxAmount, ctx);
            return cappedAmount(normalizedInner, maxAmount);
        }
        case 'surcharge': {
            const rate = evaluateNumericObservable(spec.rate, ctx);
            return { ...spec, rate: constNum(rate), amount: normalizeAmountSpec(spec.amount, ctx) };
        }
        case 'describe': {
            const normalizedInner = normalizeAmountSpec(spec.amount, ctx);
            return spec.classification === undefined
                ? describeAmount(spec.description, normalizedInner)
                : {
                    kind: 'describe',
                    description: spec.description,
                    classification: spec.classification,
                    amount: normalizedInner,
                };
        }
    }
}
function normalizePayment(payment, ctx) {
    switch (payment.kind) {
        case 'zero':
            return payment;
        case 'once': {
            const normalizedAmount = normalizeAmountSpec(payment.amount, ctx);
            return once(payment.date, normalizedAmount, payment.currency);
        }
        case 'repeat': {
            if (payment.endDate && compareDates(payment.endDate, ctx.currentDate) < 0) {
                return zero();
            }
            const normalizedAmount = normalizeAmountSpec(payment.amount, ctx);
            return repeat(payment.recurrence, normalizedAmount, payment.currency, payment.startDate, payment.endDate, payment.maxOccurrences);
        }
        case 'all': {
            const normalized = payment.payments.map((p) => normalizePayment(p, ctx));
            return simplifyAllPayments(normalized);
        }
        case 'first': {
            const normalized = payment.payments.map((p) => normalizePayment(p, ctx));
            const nonZero = normalized.filter((p) => p.kind !== 'zero');
            if (nonZero.length === 0)
                return zero();
            if (nonZero.length === 1)
                return nonZero[0];
            return { kind: 'first', payments: nonZero };
        }
        case 'notToExceed':
            return payment;
    }
}
function isConditionPermanentlyFalse(cond, ctx) {
    switch (cond.kind) {
        case 'const':
            return !cond.value;
        case 'before': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) >= 0;
        }
        case 'onOrBefore': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) > 0;
        }
        case 'and':
            return cond.conditions.some((c) => isConditionPermanentlyFalse(c, ctx));
        case 'or':
            return cond.conditions.every((c) => isConditionPermanentlyFalse(c, ctx));
        case 'context':
            return false;
        default:
            return false;
    }
}
//# sourceMappingURL=evaluate.js.map